import { $inject, $type } from '../utils/di-container.js';
import { UrlService } from './url.service.js';
import { LoggerService } from './logger.service.js';
import { HttpService } from './http.service.js';
import { FeatureService } from './feature.service.js';
import { deepClone } from '../../test/util/deep-clone.mjs';
import { base64Encode } from "../utils/base64-encode";

/**
 * @typedef {object} _ApiResponse_Product
 * @property {_BrandPartner} brand
 * @property {string} [productEan]
 * @property {boolean} [brandSponsored] brand sponsored flag - for brands with "premium" badges on the retailer counterparts
 * @property {boolean} [officialBrandShop] brand sponsored flag - for D2C badges
 */

/**
 * @typedef {object} _ApiResponse_fetchBadgeInfo
 * @property {string} [tuvApprovalDate] dd.mm.yyyy format. null if not tuv-approved
 * @property {Date} responseDate of the date in response header
 * @property {_BadgePlan} subscription
 * @property {_Retailer} retailerInfo
 * @property {_BrandPartner[]} brands
 * @property {_ApiResponse_fetchBadgeInfo_BadgeConfig[]} desktopConfiguration
 * @property {_ApiResponse_fetchBadgeInfo_BadgeConfig[]} mobileConfiguration
 * @property {_ApiResponse_fetchBadgeInfo_globalConfiguration} [globalConfiguration]
 * @property {_ApiResponse_Product} [product]
 * @property {string[]} [enabledFeatures]
 * @property {_T0Reason} [t0]
 */

/**
 * @typedef _ApiResponse_fetchBadgeInfo_BadgeConfig
 * @property {_BadgeVariant} type
 * @property {boolean} [enabled]
 * @property {number|null} [zIndex]
 * @property {string|null} [position]
 * @property {string|null} [horizontalPosition]
 * @property {{direction: string|null, distance: number|null, unit: string|null}} [positionSettings]
 * @property {{direction: string|null, distance: number|null, unit: string|null}} [horizontalPositionSettings]
 */

/**
 * @typedef _ApiResponse_fetchBadgeInfo_globalConfiguration
 * @property {boolean} hideSidebarWhenContentPresent
 * @property {string} [customCss]
 */

const t0ResponseData = {
  subscription: 'free',
  retailerInfo: {
    homepage: `${location.protocol}//${location.host}`
  },
  desktopConfiguration: [{
    type: 'mid',
    enabled: true,
    horizontalPosition: 'right',
    horizontalPositionSettings: {'distance': null, 'direction': null, 'unit': null},
    zIndex: 100000,
    position: 'middle',
    positionSettings: {'distance': null, 'direction': null, 'unit': null}
  }],
  mobileConfiguration: [{
    type: 'mid',
    enabled: true,
    horizontalPosition: 'right',
    horizontalPositionSettings: {'distance': null, 'direction': null, 'unit': null},
    zIndex: 100000,
    position: 'bottom',
    positionSettings: {'distance': null, 'direction': null, 'unit': null}
  }],
  brands: []
};

/**
 * Used to communicate to the badge B/E service to obtain information about expected badges, brands and configuration.
 */
export class Api2Service {

  /** @type {LoggerService} */
  _logger;

  /** @type {UrlService} */
  _urlService;

  /** @type {HttpService} */
  _http;

  /** @type {FeatureService} */
  _featureService;

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
    this._urlService = obtain(UrlService);
    this._http = obtain(HttpService);
    this._featureService = obtain(FeatureService);
  }

  /**
   * @param {_BadgeMeta|any} badgeMeta
   * @param {any} outboundData
   * @returns {Promise<_ApiResponse_fetchBadgeInfo>}
   */
  async fetchBadgeInfo(badgeMeta, outboundData) {
    // trim productId and excluding sending it if it has an invalid value
    const params = {};
    const headers = {};

    let productId = badgeMeta?.productId || outboundData?.productId || undefined;
    if (productId) {
      productId = productId.trim();
    }
    let brandName = badgeMeta?.brandName || outboundData?.brandName || undefined;
    if (productId) {
      params.productId = productId;
    }
    if (brandName) {
      params.brand = brandName;
    }
    if (outboundData) {
      params.data = base64Encode(JSON.stringify(outboundData));
    }
    if (badgeMeta.environment === "app") {
      const authToken = localStorage.getItem("auth_token");
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }
    }
    params.source = base64Encode(JSON.stringify({
      productId: badgeMeta?.productId ? 'user' : outboundData?.productId ? 'auto' : 'none',
      brandName: badgeMeta?.brandName ? 'user' : outboundData?.brandName ? 'auto' : 'none'
    }));

    if(window.location.pathname.length < 2000) {
      headers['X-Referrer'] = window.location.pathname;
    }

    const url = this._urlService.badge2ApiUrl(badgeMeta.environment);
    const res = await this._http.get(url, params, headers);

    if (res.error) {
      this._logger.error('api/fetch/error', `Fetch badge info error: ${res.error.type} | ${res.error.code} | ${res.error.details}`, () => ({ badgeMeta: deepClone(badgeMeta), url, params, error: res.error }));

      this._featureService.setFeatures(res.error.enabledFeatures || []);

      const t0 = res.error.code === '204' ? 'no-account' : res.error.code === '202' ? 'not-approved' : null;
      if (t0) {
        return {
          responseDate: res.responseDate,
          ...t0ResponseData,
          t0
        };
      }
    } else {
      if (res.data && res.data.product && res.data.product.error) {
        this._logger.warn('api/product/error', `Badge product error: ${JSON.stringify(res.data.product.error)}`, () => ({ badgeMeta: deepClone(badgeMeta), error: res.data.product.error }));
      }

      this._featureService.setFeatures(res.data.enabledFeatures);

      // t0 for 0-partners is used only when there is at least a badge config enabled
      const hasSomeBadgeConfig = (res.data.desktopConfiguration || []).concat(res.data.mobileConfiguration || []).some(bc => bc.enabled);
      const t0 = (res.data.brands || []).length === 0 ? 'no-brands' : null;
      if (t0 && hasSomeBadgeConfig) {
        return {
          responseDate: res.responseDate,
          ...t0ResponseData,
          t0
        };
      }

      if (res.data.product) {
        res.data.product.id = res.data.product.productEan;
      }

      return {
        responseDate: res.responseDate,
        ...res.data
      };
    }
  }

}

Api2Service[$type] = Symbol('Api2Service');
